/**
 * FavoritesManager — Manages favorite radio stations
 * 
 * Persists favorites in localStorage.
 * Emits custom events so other modules can react to changes.
 */

const FAVORITES_KEY = 'wwr_favorites';

class FavoritesManager {
  constructor() {
    this._favorites = this._load();
    this._listeners = [];
  }

  /**
   * Check if a station is favorited
   * @param {string} stationId 
   * @returns {boolean}
   */
  isFavorite(stationId) {
    return this._favorites.has(String(stationId));
  }

  /**
   * Toggle favorite status for a station
   * @param {string} stationId 
   * @returns {boolean} new favorite state
   */
  toggle(stationId) {
    const id = String(stationId);
    if (this._favorites.has(id)) {
      this._favorites.delete(id);
    } else {
      this._favorites.add(id);
    }
    this._save();
    this._emit(id, this._favorites.has(id));
    return this._favorites.has(id);
  }

  /**
   * Add a favorite
   * @param {string} stationId 
   */
  add(stationId) {
    const id = String(stationId);
    if (!this._favorites.has(id)) {
      this._favorites.add(id);
      this._save();
      this._emit(id, true);
    }
  }

  /**
   * Remove a favorite
   * @param {string} stationId 
   */
  remove(stationId) {
    const id = String(stationId);
    if (this._favorites.has(id)) {
      this._favorites.delete(id);
      this._save();
      this._emit(id, false);
    }
  }

  /**
   * Get all favorite station IDs
   * @returns {string[]}
   */
  getAll() {
    return [...this._favorites];
  }

  /**
   * Get count of favorites
   * @returns {number}
   */
  get count() {
    return this._favorites.size;
  }

  /**
   * Filter a list of stations to only favorites
   * @param {Array} stations 
   * @returns {Array}
   */
  filterFavorites(stations) {
    return stations.filter(s => this._favorites.has(String(s.id)));
  }

  /**
   * Subscribe to favorite changes
   * @param {Function} callback - (stationId, isFavorite) => void
   * @returns {Function} unsubscribe function
   */
  onChange(callback) {
    this._listeners.push(callback);
    return () => {
      this._listeners = this._listeners.filter(fn => fn !== callback);
    };
  }

  // ─── Internal ───────────────────────────────────────────────────────────

  _load() {
    try {
      const raw = localStorage.getItem(FAVORITES_KEY);
      if (!raw) return new Set();
      return new Set(JSON.parse(raw));
    } catch {
      return new Set();
    }
  }

  _save() {
    try {
      localStorage.setItem(FAVORITES_KEY, JSON.stringify([...this._favorites]));
    } catch {
      // localStorage full or unavailable
    }
  }

  _emit(stationId, isFavorite) {
    this._listeners.forEach(fn => fn(stationId, isFavorite));
  }
}

// Singleton
export const favoritesManager = new FavoritesManager();
